import { getApiBaseUrl } from "../config/apiBase";
import { apiFetch } from "../config/apiFetch";

export interface GstTaxpayerDetails {
  gstin: string;
  legalName: string;
  tradeName: string;
  constitutionOfBusiness: string;
  taxpayerType: string;
  gstStatus: string;
  registrationDate: string;
  stateJurisdiction: string;
  centreJurisdiction: string;
  principalAddress: string;
  additionalAddresses: string[];
  natureOfBusinessActivities: string[];
  einvoiceEnabled: boolean;
  /** Best display name: tradeName if set, else legalName */
  displayName: string;
  /** True when GST portal returned HTML/CAPTCHA but the GSTIN check digit is valid — enter firm details manually. */
  portalLookupFailed?: boolean;
  checksumValid?: boolean;
}

/** 15-char GSTIN pattern check (client-side, before hitting the server). */
export const GSTIN_PATTERN = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;

export function isValidGstin(value: string): boolean {
  return GSTIN_PATTERN.test(value.trim().toUpperCase());
}

export class GstPortalBlockedError extends Error {
  readonly portalBlocked = true;
  constructor(message: string) {
    super(message);
    this.name = "GstPortalBlockedError";
  }
}

export async function verifyGstin(gstin: string): Promise<GstTaxpayerDetails> {
  const res = await apiFetch(
    `${getApiBaseUrl()}/api/gst/verify/${encodeURIComponent(gstin.trim().toUpperCase())}`,
    { cache: "no-store" },
  );
  if (!res.ok) {
    let msg = `GST lookup failed (${res.status})`;
    let blocked = false;
    try {
      const j = (await res.json()) as { error?: string; portalBlocked?: boolean };
      if (j.error) msg = j.error;
      if (j.portalBlocked) blocked = true;
    } catch { /* ignore */ }
    if (blocked) throw new GstPortalBlockedError(msg);
    throw new Error(msg);
  }
  return res.json() as Promise<GstTaxpayerDetails>;
}

/** Starts a GST portal browser session and returns CAPTCHA image (same flow as gst.gov.in search). */
export async function fetchGstCaptchaSession(): Promise<{ sessionId: string; imageDataUrl: string }> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/gst/captcha-session`, { cache: "no-store" });
  if (!res.ok) {
    let msg = "Could not load CAPTCHA from GST portal.";
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json() as Promise<{ sessionId: string; imageDataUrl: string }>;
}

/** POST taxpayer search with CAPTCHA (uses session from {@link fetchGstCaptchaSession}). */
export async function verifyGstinWithCaptcha(
  sessionId: string,
  gstin: string,
  captcha: string,
): Promise<GstTaxpayerDetails> {
  const res = await apiFetch(`${getApiBaseUrl()}/api/gst/verify-captcha`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      sessionId,
      gstin: gstin.trim().toUpperCase(),
      captcha: captcha.trim(),
    }),
    cache: "no-store",
  });
  if (!res.ok) {
    let msg = `GST verification failed (${res.status})`;
    try {
      const j = (await res.json()) as { error?: string };
      if (j.error) msg = j.error;
    } catch { /* ignore */ }
    throw new Error(msg);
  }
  return res.json() as Promise<GstTaxpayerDetails>;
}
